// Role-driven profile schemas. Each section lists fields displayed on the
// profile page for a given role. The set mirrors what each Register* page
// captures at signup. Public defaults indicate whether the field is visible
// to other users (sellers/brokers/buyers) when no explicit visibility is set.

export type FieldType =
  | "text"
  | "email"
  | "tel"
  | "textarea"
  | "select"
  | "multiselect"
  | "tags"
  | "number"
  | "date"
  | "checkbox";

export type FieldDef = {
  key: string;            // dot-path inside profile data (e.g. "address.city")
  label: { en: string; fr: string };
  type: FieldType;
  options?: string[];     // for select/multiselect
  placeholder?: string;
  publicDefault?: boolean; // visible to other roles by default
  readonly?: boolean;
  rows?: number;
};

export type SectionDef = {
  id: string;
  title: { en: string; fr: string };
  fields: FieldDef[];
};

export type RoleKey = "buyer" | "seller" | "consultant" | "broker" | "lender" | "partner";

export type RoleSchema = {
  role: RoleKey;
  title: { en: string; fr: string };
  sections: SectionDef[];
};

const identityFields: FieldDef[] = [
  { key: "firstName", label: { en: "First name", fr: "Prénom" }, type: "text", publicDefault: true },
  { key: "lastName", label: { en: "Last name", fr: "Nom" }, type: "text", publicDefault: true },
  { key: "email", label: { en: "Email", fr: "Courriel" }, type: "email", readonly: true, publicDefault: false },
  { key: "phone", label: { en: "Phone", fr: "Téléphone" }, type: "tel", publicDefault: false },
];

const addressFields: FieldDef[] = [
  { key: "address.street", label: { en: "Street", fr: "Adresse" }, type: "text", publicDefault: false },
  { key: "address.city", label: { en: "City", fr: "Ville" }, type: "text", publicDefault: true },
  { key: "address.stateProvince", label: { en: "State / Province", fr: "Province / État" }, type: "text", publicDefault: true },
  { key: "address.zipPostalCode", label: { en: "ZIP / Postal", fr: "Code postal" }, type: "text", publicDefault: false },
  { key: "address.country", label: { en: "Country", fr: "Pays" }, type: "text", publicDefault: true },
];

export const ROLE_SCHEMAS: Record<RoleKey, RoleSchema> = {
  buyer: {
    role: "buyer",
    title: { en: "Buyer profile", fr: "Profil acheteur" },
    sections: [
      { id: "identity", title: { en: "Identity", fr: "Identité" }, fields: identityFields },
      { id: "address", title: { en: "Address", fr: "Adresse" }, fields: addressFields },
      {
        id: "about",
        title: { en: "About you", fr: "À propos" },
        fields: [
          { key: "selfDescription", label: { en: "Self description", fr: "Description" }, type: "textarea", rows: 4, publicDefault: true },
          { key: "experience", label: { en: "Acquisition experience", fr: "Expérience d'acquisition" }, type: "textarea", rows: 3, publicDefault: true },
        ],
      },
      {
        id: "preferences",
        title: { en: "Acquisition preferences", fr: "Préférences d'acquisition" },
        fields: [
          { key: "registrationData.budgetMin", label: { en: "Budget min ($)", fr: "Budget min ($)" }, type: "number", publicDefault: false },
          {
            key: "registrationData.budgetMin",
            label: { en: "Share budget range publicly", fr: "Partager la fourchette de budget" },
            type: "checkbox",
            publicDefault: false
          },
          { key: "registrationData.budgetMax", label: { en: "Budget max ($)", fr: "Budget max ($)" }, type: "number", publicDefault: false },
          { key: "registrationData.industriesOfInterest", label: { en: "Industries of interest", fr: "Secteurs d'intérêt" }, type: "tags", publicDefault: true },
          { key: "registrationData.preferredLocations", label: { en: "Preferred locations", fr: "Localisations préférées" }, type: "tags", publicDefault: true },
          { key: "registrationData.timeline", label: { en: "Acquisition timeline", fr: "Échéancier" }, type: "text", publicDefault: true },
          { key: "registrationData.fundingSource", label: { en: "Funding source", fr: "Source de financement" }, type: "text", publicDefault: false },
        ],
      },
      {
        id: "subscription",
        title: { en: "Subscription", fr: "Abonnement" },
        fields: [
          { key: "subscriptionTier", label: { en: "Plan", fr: "Plan" }, type: "text", readonly: true, publicDefault: false },
        ],
      },
    ],
  },
  seller: {
    role: "seller",
    title: { en: "Seller profile", fr: "Profil vendeur" },
    sections: [
      { id: "identity", title: { en: "Identity", fr: "Identité" }, fields: identityFields },
      { id: "address", title: { en: "Address", fr: "Adresse" }, fields: addressFields },
      {
        id: "company",
        title: { en: "Company", fr: "Entreprise" },
        fields: [
          { key: "companyName", label: { en: "Company name", fr: "Nom de l'entreprise" }, type: "text", publicDefault: true },
          { key: "industry", label: { en: "Industry", fr: "Secteur" }, type: "text", publicDefault: true },
          { key: "yearsInBusiness", label: { en: "Years in business", fr: "Années d'activité" }, type: "number", publicDefault: true },
          { key: "employees", label: { en: "Employees", fr: "Employés" }, type: "number", publicDefault: true },
          { key: "website", label: { en: "Website", fr: "Site web" }, type: "text", publicDefault: true },
        ],
      },
      {
        id: "deal",
        title: { en: "Deal context", fr: "Contexte de la transaction" },
        fields: [
          { key: "askingPrice", label: { en: "Asking price ($)", fr: "Prix demandé ($)" }, type: "number", publicDefault: false },
          { key: "annualRevenue", label: { en: "Annual revenue ($)", fr: "Revenus annuels ($)" }, type: "number", publicDefault: false },
          { key: "ebitda", label: { en: "EBITDA ($)", fr: "BAIIA ($)" }, type: "number", publicDefault: false },
          { key: "reasonForSelling", label: { en: "Reason for selling", fr: "Raison de la vente" }, type: "textarea", rows: 3, publicDefault: false },
          { key: "documentsReady", label: { en: "Documents ready?", fr: "Documents prêts ?" }, type: "text", publicDefault: false },
          { key: "firstSale", label: { en: "First sale?", fr: "Première vente ?" }, type: "text", publicDefault: false },
          { key: "needGuidance", label: { en: "Needs guidance?", fr: "Besoin d'accompagnement ?" }, type: "text", publicDefault: false },
        ],
      },
    ],
  },
  broker: {
    role: "broker",
    title: { en: "Broker profile", fr: "Profil broker" },
    sections: [
      { id: "identity", title: { en: "Identity", fr: "Identité" }, fields: identityFields },
      { id: "address", title: { en: "Address", fr: "Adresse" }, fields: addressFields },
      {
        id: "firm",
        title: { en: "Firm & Professional Info", fr: "Cabinet & Informations professionnelles" },
        fields: [
          { key: "firmName", label: { en: "Firm name", fr: "Nom du cabinet" }, type: "text", publicDefault: true },
          { key: "registrationData.professionalDescription", label: { en: "Professional description", fr: "Description professionnelle" }, type: "textarea", rows: 4, publicDefault: true },
        ],
      },
      {
        id: "expertise",
        title: { en: "Expertise & Sectors", fr: "Expertise & Secteurs" },
        fields: [
          { key: "registrationData.specializations.0.industry", label: { en: "Primary sector", fr: "Secteur principal" }, type: "text", publicDefault: true },
          { key: "registrationData.specializations.0.experienceYears", label: { en: "Years of experience", fr: "Années d'expérience" }, type: "text", publicDefault: true },
          { key: "registrationData.dealSizeMin", label: { en: "Min deal size", fr: "Taille min des transactions" }, type: "text", publicDefault: true },
          { key: "registrationData.dealSizeMax", label: { en: "Max deal size", fr: "Taille max des transactions" }, type: "text", publicDefault: true },
          { key: "registrationData.feeStructure", label: { en: "Fee structure", fr: "Structure des honoraires" }, type: "tags", publicDefault: true },
        ],
      },
      {
        id: "license",
        title: { en: "License & Jurisdictions", fr: "Licence & Juridictions" },
        fields: [
          { key: "registrationData.licenses.0.details", label: { en: "License details", fr: "Détails de la licence" }, type: "textarea", rows: 2, publicDefault: true },
          { key: "registrationData.licenses.0.number", label: { en: "License number", fr: "Numéro de licence" }, type: "text", publicDefault: true },
          { key: "registrationData.licenses.0.otherJurisdictions", label: { en: "Other jurisdictions", fr: "Autres juridictions" }, type: "text", publicDefault: true },
        ],
      },
      {
        id: "languages",
        title: { en: "Languages", fr: "Langues" },
        fields: [
          { key: "registrationData.motherTongue", label: { en: "Mother tongue", fr: "Langue maternelle" }, type: "text", publicDefault: true },
          { key: "registrationData.spokenLanguages", label: { en: "Languages spoken", fr: "Langues parlées" }, type: "tags", publicDefault: true },
        ],
      },
    ],
  },
  consultant: {
    role: "consultant",
    title: { en: "Consultant profile", fr: "Profil consultant" },
    sections: [
      { id: "identity", title: { en: "Identity", fr: "Identité" }, fields: identityFields },
      { id: "address", title: { en: "Address", fr: "Adresse" }, fields: addressFields },
      {
        id: "company",
        title: { en: "Company", fr: "Entreprise" },
        fields: [
          { key: "companyName", label: { en: "Company name", fr: "Nom de l'entreprise" }, type: "text", publicDefault: true },
        ],
      },
      {
        id: "expertise",
        title: { en: "Expertise", fr: "Expertise" },
        fields: [
          { key: "registrationData.specializations.0.industry", label: { en: "Primary sector", fr: "Secteur principal" }, type: "text", publicDefault: true },
          { key: "registrationData.specializations.0.experienceYears", label: { en: "Years of experience", fr: "Années d'expérience" }, type: "text", publicDefault: true },
          { key: "registrationData.services", label: { en: "Services offered", fr: "Services offerts" }, type: "textarea", rows: 3, publicDefault: true },
          { key: "registrationData.compensationOptions", label: { en: "Compensation options", fr: "Options de rémunération" }, type: "tags", publicDefault: true },
          { key: "registrationData.hourlyRate", label: { en: "Hourly rate ($)", fr: "Taux horaire ($)" }, type: "number", publicDefault: true },
          { key: "registrationData.availability", label: { en: "Availability", fr: "Disponibilité" }, type: "text", publicDefault: true },
        ],
      },
      {
        id: "languages",
        title: { en: "Languages", fr: "Langues" },
        fields: [
          { key: "registrationData.motherTongue", label: { en: "Mother tongue", fr: "Langue maternelle" }, type: "text", publicDefault: true },
          { key: "registrationData.spokenLanguages", label: { en: "Languages spoken", fr: "Langues parlées" }, type: "tags", publicDefault: true },
        ],
      },
      {
        id: "background",
        title: { en: "Background check", fr: "Vérification des antécédents" },
        fields: [
          { key: "status", label: { en: "Account status", fr: "Statut du compte" }, type: "text", readonly: true, publicDefault: false },
        ],
      },
    ],
  },
  lender: {
    role: "lender",
    title: { en: "Lender profile", fr: "Profil prêteur" },
    sections: [
      { id: "identity", title: { en: "Identity", fr: "Identité" }, fields: identityFields },
      { id: "address", title: { en: "Address", fr: "Adresse" }, fields: addressFields },
      {
        id: "company",
        title: { en: "Company", fr: "Entreprise" },
        fields: [
          { key: "registrationData.companyName", label: { en: "Company name", fr: "Nom de l'entreprise" }, type: "text", publicDefault: true },
        ],
      },
      {
        id: "lending",
        title: { en: "Lending profile", fr: "Profil de financement" },
        fields: [
          { key: "registrationData.lenderTypes", label: { en: "Lender types", fr: "Types de prêteur" }, type: "tags", publicDefault: true },
          { key: "registrationData.loanProductTypes", label: { en: "Loan product types", fr: "Types de produits" }, type: "tags", publicDefault: true },
          { key: "registrationData.collateralOptions", label: { en: "Collateral options", fr: "Garanties acceptées" }, type: "tags", publicDefault: true },
          { key: "registrationData.bio", label: { en: "About", fr: "À propos" }, type: "textarea", rows: 4, publicDefault: true },
        ],
      },
      {
        id: "lendingCriteria",
        title: { en: "Lending criteria", fr: "Critères de financement" },
        fields: [
          { key: "registrationData.minRange", label: { en: "Min loan amount ($)", fr: "Montant min ($)" }, type: "text", publicDefault: true },
          { key: "registrationData.maxRange", label: { en: "Max loan amount ($)", fr: "Montant max ($)" }, type: "text", publicDefault: true },
          { key: "registrationData.minGrossRevenue", label: { en: "Min gross revenue ($)", fr: "Revenu brut min ($)" }, type: "text", publicDefault: false },
          { key: "registrationData.maxGrossRevenue", label: { en: "Max gross revenue ($)", fr: "Revenu brut max ($)" }, type: "text", publicDefault: false },
          { key: "registrationData.minEbitda", label: { en: "Min EBITDA ($)", fr: "BAIIA min ($)" }, type: "text", publicDefault: false },
          { key: "registrationData.maxEbitda", label: { en: "Max EBITDA ($)", fr: "BAIIA max ($)" }, type: "text", publicDefault: false },
          { key: "registrationData.minCashFlow", label: { en: "Min cash flow ($)", fr: "Flux de trésorerie min ($)" }, type: "text", publicDefault: false },
          { key: "registrationData.maxCashFlow", label: { en: "Max cash flow ($)", fr: "Flux de trésorerie max ($)" }, type: "text", publicDefault: false },
          { key: "registrationData.debtServiceCoverageRatio", label: { en: "DSCR", fr: "Ratio DSCR" }, type: "text", publicDefault: true },
          { key: "registrationData.creditScoreRequired", label: { en: "Credit score required", fr: "Cote de crédit requise" }, type: "text", publicDefault: true },
          { key: "registrationData.foreignCreditScoreAccepted", label: { en: "Foreign credit score accepted", fr: "Cote de crédit étrangère acceptée" }, type: "text", publicDefault: false },
          { key: "registrationData.foreignCollateralAccepted", label: { en: "Foreign collateral accepted", fr: "Garantie étrangère acceptée" }, type: "text", publicDefault: false },
          { key: "registrationData.foreignCollateralOptions", label: { en: "Foreign collateral options", fr: "Options de garantie étrangère" }, type: "tags", publicDefault: false },
        ],
      },
      {
        id: "jurisdictions",
        title: { en: "Jurisdictions", fr: "Juridictions" },
        fields: [
          { key: "registrationData.jurisdiction_canada", label: { en: "Canada", fr: "Canada" }, type: "tags", publicDefault: true },
          { key: "registrationData.jurisdiction_usa", label: { en: "USA", fr: "États-Unis" }, type: "tags", publicDefault: true },
          { key: "registrationData.jurisdiction_europe", label: { en: "Europe", fr: "Europe" }, type: "tags", publicDefault: true },
          { key: "registrationData.jurisdiction_others", label: { en: "Other regions", fr: "Autres régions" }, type: "tags", publicDefault: true },
        ],
      },
    ],
  },
  partner: {
    role: "partner",
    title: { en: "Internal Partner profile", fr: "Profil Partenaire Interne" },
    sections: [
      { id: "identity", title: { en: "Identity", fr: "Identité" }, fields: identityFields },
      { id: "address", title: { en: "Address", fr: "Adresse" }, fields: addressFields },
      {
        id: "expertise",
        title: { en: "Areas of Expertise", fr: "Domaines d'expertise" },
        fields: [
          { key: "registrationData.specializations.0.industry", label: { en: "Primary sector", fr: "Secteur principal" }, type: "text", publicDefault: true },
          { key: "registrationData.specializations.0.experienceYears", label: { en: "Years of experience", fr: "Années d'expérience" }, type: "text", publicDefault: true },
          { key: "registrationData.dealSizeMin", label: { en: "Min deal size", fr: "Taille min des transactions" }, type: "text", publicDefault: true },
          { key: "registrationData.dealSizeMax", label: { en: "Max deal size", fr: "Taille max des transactions" }, type: "text", publicDefault: true },
          { key: "registrationData.feeStructure", label: { en: "Fee structure", fr: "Structure des honoraires" }, type: "tags", publicDefault: true },
        ],
      },
      {
        id: "license",
        title: { en: "Professional License", fr: "Licence professionnelle" },
        fields: [
          { key: "registrationData.licenses.0.details", label: { en: "License / Permit details", fr: "Détails de la licence" }, type: "textarea", rows: 2, publicDefault: true },
          { key: "registrationData.licenses.0.number", label: { en: "Active license number", fr: "Numéro de licence actif" }, type: "text", publicDefault: true },
          { key: "registrationData.licenses.0.otherJurisdictions", label: { en: "Other jurisdictions", fr: "Autres juridictions" }, type: "text", publicDefault: true },
        ],
      },
      {
        id: "languages",
        title: { en: "Languages", fr: "Langues" },
        fields: [
          { key: "registrationData.motherTongue", label: { en: "Mother tongue", fr: "Langue maternelle" }, type: "text", publicDefault: true },
          { key: "registrationData.spokenLanguages", label: { en: "Languages spoken", fr: "Langues parlées" }, type: "tags", publicDefault: true },
        ],
      },
      {
        id: "services",
        title: { en: "Partner Services", fr: "Services Partenaire" },
        fields: [
          { key: "partnerSubscription.addons", label: { en: "Active services", fr: "Services actifs" }, type: "tags", readonly: true, publicDefault: true },
          { key: "status", label: { en: "Account status", fr: "Statut du compte" }, type: "text", readonly: true, publicDefault: false },
        ],
      },
    ],
  },
};

// Helper: get/set a value at a dot-path inside an object.
export function getPath(obj: any, path: string): any {
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}
export function setPath(obj: any, path: string, value: any): any {
  const keys = path.split(".");
  const next = { ...(obj || {}) };
  let cur: any = next;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = { ...(cur[keys[i]] || {}) };
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return next;
}
